/**
 * Session Restoration Utilities
 * Handles fetching, comparing, and restoring sessions from cloud and local storage
 *
 * Updated to support per-locale session storage with session IDs that include
 * locale prefix (e.g., 'en-1234567890-uuid')
 */

import { restoreSession } from '@/store/hybridStorage'
import { localeCodes, defaultLocale } from '@/i18n/locales'

// Valid locales for session ID parsing - imported from centralized config
const VALID_LOCALES = localeCodes;
const DEFAULT_LOCALE = defaultLocale;

/**
 * Extract locale from a session ID
 * Formats:
 * - Simple locale: 'en-1703123456789-abc123'
 * - Compound locale: 'es-US-1703123456789-abc123'
 * - Legacy format: '1703123456789-abc123' (no locale prefix)
 *
 * @param {string} sessionId - The session ID to parse
 * @returns {string | null} - The locale code or null if not found/legacy format
 */
export const getLocaleFromSessionId = (sessionId) => {
  if (!sessionId) return null;

  // Try to match each valid locale at the beginning of the session ID
  for (const locale of VALID_LOCALES) {
    if (sessionId.startsWith(`${locale}-`)) {
      // Verify the next part looks like a timestamp (all digits)
      const rest = sessionId.slice(locale.length + 1);
      const nextPart = rest.split('-')[0];
      if (/^\d+$/.test(nextPart)) {
        return locale;
      }
    }
  }

  // Legacy session without locale prefix
  return null;
};

/**
 * Check if a session ID is in the legacy format (no locale prefix)
 * @param {string} sessionId - The session ID to check
 * @returns {boolean} - True if legacy format
 */
export const isLegacySessionId = (sessionId) => {
  return getLocaleFromSessionId(sessionId) === null;
};

/**
 * Fetch cloud session data using the session ID
 * @param {string} sessionId - The session ID to restore
 * @returns {Promise<{success: boolean, data: any, error?: string}>}
 */
export const fetchCloudSession = async (sessionId) => {
  if (!sessionId) {
    console.error('❌ fetchCloudSession: No session ID provided');
    return { success: false, error: 'No session ID provided' }
  }

  console.log('🌐 Fetching cloud session from API for sessionId:', sessionId);

  try {
    const cloudData = await restoreSession(sessionId)
    console.log('📦 API Response:', cloudData);

    // API can return data in two formats:
    // 1. Zustand persist format: { state: { currentStep, formData, ... }, version: 0 }
    // 2. Direct state object: { currentStep, formData, ... }
    if (cloudData) {
      // Check for wrapped format (Zustand persist)
      if (cloudData.state && (cloudData.state.formData || cloudData.state.currentStep !== undefined)) {
        console.log('✅ Valid cloud data found (wrapped format)');
        return { success: true, data: cloudData }
      }
      // Check for unwrapped format
      else if (cloudData.formData || cloudData.currentStep !== undefined) {
        console.log('✅ Valid cloud data found (unwrapped format)');
        return { success: true, data: cloudData }
      }
      else {
        console.warn('⚠️ Cloud data exists but missing essential fields');
        console.log('Cloud data structure:', cloudData);
      }
    } else {
      console.log('ℹ️ Cloud data is null or undefined');
    }

    return { success: false, error: 'No cloud backup found' }
  } catch (error) {
    console.error('❌ Exception while fetching cloud session:', error)
    return { success: false, error: error.message || 'Failed to fetch cloud session' }
  }
}

/**
 * Get local session data from localStorage for a specific locale
 * @param {string} locale - The locale code (e.g., 'en-us')
 * @returns {any | null} - Local session data or null if not found
 */
export const getLocalSession = (locale = DEFAULT_LOCALE) => {
  try {
    const storageKey = `book-pro-storage-${locale}`;
    const localData = localStorage.getItem(storageKey)
    if (!localData) return null

    const parsed = JSON.parse(localData)
    return parsed
  } catch (error) {
    console.error(`Error reading local session for ${locale}:`, error)
    return null
  }
}

/**
 * Get legacy local session data (without locale prefix)
 * Used for migration purposes
 * @returns {any | null} - Legacy local session data or null if not found
 */
export const getLegacyLocalSession = () => {
  try {
    const localData = localStorage.getItem('book-pro-storage')
    if (!localData) return null

    const parsed = JSON.parse(localData)
    return parsed
  } catch (error) {
    console.error('Error reading legacy local session:', error)
    return null
  }
}

/**
 * Generate a hash from session data for comparison
 * @param {any} sessionData - The session data to hash
 * @returns {string} - Hash string
 */
const generateSessionHash = (sessionData) => {
  if (!sessionData) return ''

  // Handle both formats: direct state or wrapped with state property
  const stateData = sessionData.state ? sessionData.state : sessionData;

  if (!stateData.formData) return ''

  // Create a normalized object with only the data we want to compare
  const compareData = {
    currentStep: stateData.currentStep,
    formData: stateData.formData,
    selectedTemplate: stateData.selectedTemplate,
    praises: stateData.praises,
    tableOfContents: stateData.tableOfContents,
    // Add other relevant fields but exclude sessionId and timestamps
  }

  // Simple hash function - in production, consider using crypto.subtle.digest
  const str = JSON.stringify(compareData)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return hash.toString(36)
}

/**
 * Compare two sessions by their content hash
 * @param {any} session1 - First session data
 * @param {any} session2 - Second session data
 * @returns {boolean} - True if sessions are equal
 */
export const compareSessionsHash = (session1, session2) => {
  if (!session1 || !session2) return false

  const hash1 = generateSessionHash(session1)
  const hash2 = generateSessionHash(session2)

  return hash1 === hash2 && hash1 !== ''
}

/**
 * Clear local session data for a specific locale
 * @param {string} locale - The locale code (e.g., 'en-us')
 * @returns {boolean} - Success status
 */
export const clearLocalSession = (locale = DEFAULT_LOCALE) => {
  try {
    const storageKey = `book-pro-storage-${locale}`;
    localStorage.removeItem(storageKey)
    return true
  } catch (error) {
    console.error(`Error clearing local session for ${locale}:`, error)
    return false
  }
}

/**
 * Clear legacy local session data (without locale prefix)
 * Used after successful migration
 * @returns {boolean} - Success status
 */
export const clearLegacyLocalSession = () => {
  try {
    localStorage.removeItem('book-pro-storage')
    return true
  } catch (error) {
    console.error('Error clearing legacy local session:', error)
    return false
  }
}

/**
 * Migrate legacy session to locale-specific storage
 * @param {string} targetLocale - The locale to migrate to
 * @returns {boolean} - True if migration occurred, false if not needed
 */
export const migrateLegacySession = (targetLocale = DEFAULT_LOCALE) => {
  try {
    const legacyData = getLegacyLocalSession();
    const newStorageKey = `book-pro-storage-${targetLocale}`;
    const existingData = localStorage.getItem(newStorageKey);

    // Only migrate if legacy data exists and no locale-specific data exists
    if (legacyData && !existingData) {
      console.log(`📦 Migrating legacy session to ${targetLocale}`);
      localStorage.setItem(newStorageKey, JSON.stringify(legacyData));
      // Don't clear legacy data immediately in case user switches locales
      // clearLegacyLocalSession();
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error migrating legacy session:', error);
    return false;
  }
}

/**
 * Main restoration flow handler
 * @param {string} sessionId - Session ID to restore
 * @param {string} locale - Current locale for local session lookup
 * @returns {Promise<{
 *   status: 'no_local' | 'sessions_equal' | 'sessions_differ' | 'cloud_failed' | 'error',
 *   cloudData?: any,
 *   localData?: any,
 *   error?: string
 * }>}
 */
export const checkRestoreStatus = async (sessionId, locale = DEFAULT_LOCALE) => {
  console.log(`🔄 Starting session restoration for sessionId: ${sessionId}, locale: ${locale}`);

  // Fetch cloud session
  const cloudResult = await fetchCloudSession(sessionId)

  if (!cloudResult.success) {
    console.error('❌ Cloud fetch failed:', cloudResult.error);
    const localData = getLocalSession(locale)
    if (localData) {
      console.log('✅ Local session found, offering fallback');
      return {
        status: 'cloud_failed',
        localData,
        error: cloudResult.error
      }
    }
    console.error('❌ No local or cloud session available');
    return {
      status: 'error',
      error: cloudResult.error || 'No cloud or local session found'
    }
  }

  console.log('✅ Cloud session fetched successfully');
  console.log('Cloud data:', cloudResult.data);

  // Get local session for current locale
  const localData = getLocalSession(locale)

  // No local session - restore cloud automatically
  if (!localData || !localData.state) {
    console.log('ℹ️ No local session found, will restore from cloud automatically');
    return {
      status: 'no_local',
      cloudData: cloudResult.data
    }
  }

  console.log('✅ Local session found');
  console.log('Local data:', localData);

  // Compare sessions
  const areEqual = compareSessionsHash(cloudResult.data, localData)

  if (areEqual) {
    console.log('ℹ️ Sessions are identical');
    return {
      status: 'sessions_equal',
      cloudData: cloudResult.data,
      localData
    }
  } else {
    console.log('ℹ️ Sessions differ');
    return {
      status: 'sessions_differ',
      cloudData: cloudResult.data,
      localData
    }
  }
}
